import { GroundTruths, useDialog } from "@app/shared";
import { FC, useState } from "react";
import JsonViewer from "../../shared/JsonViewer";

type CallerPayloadLinkProps = {
    jobId: string;
};

/**
 * "View response JSON" link plus the dialog it opens, showing exactly what the synchronous upload
 * route returns for this job — the whole ApiResponse envelope, not just its data.
 *
 * Shown whether or not anyone actually received it. A dashboard upload goes through the async
 * route and has no caller waiting on an answer, so this is the only way to see what an integration
 * posting the same photos would have got back — which is the thing people are writing against.
 *
 * Fetched on click rather than with the rest of the detail: it is a second round trip that most
 * openings of this dialog never need, and the payload is built server-side precisely so the bytes
 * shown are the bytes sent. Re-serializing a typed object here with the client's own conventions
 * would quietly show camelCase where the wire has something else.
 */
export const CallerPayloadLink: FC<CallerPayloadLinkProps> = ({ jobId }) => {
    const payloadDialog = useDialog<unknown>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const open = async () => {
        setIsLoading(true);
        setError(undefined);
        try {
            const raw = await GroundTruths.getCallerPayload(jobId);
            if (!raw) throw new Error("The server returned no payload for this job.");

            // The endpoint hands back the envelope as a string so its exact formatting survives the
            // trip; JsonViewer wants an object, so it is parsed only here, at the point of display.
            payloadDialog.show("Response JSON", "Close", JSON.parse(raw), () => { });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="text-center pt-2">
                <a
                    href="#"
                    className="text-xs text-brand-primary hover:underline focus:outline-none"
                    onClick={(e) => { e.preventDefault(); if (!isLoading) void open(); }}
                >
                    {isLoading ? "Loading…" : "View response JSON"}
                </a>
                {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            </div>

            {payloadDialog.renderDialog(
                (data) => (
                    <div className="max-h-[60vh] overflow-auto space-y-2">
                        <p className="text-xs text-semi-bright-text">
                            What <code>POST /api/groundtruth/&#123;facilityId&#125;</code> returns for this run.
                            Dashboard uploads use the async route, so this is what an integration would
                            have received rather than what anyone did.
                        </p>
                        <JsonViewer data={data} />
                    </div>
                ),
                {
                    className: "w-[75%] overflow-hidden",
                    closeOnOutsideClick: true,
                    // Read-only: renderDialog adds a Cancel button whenever an onOk is supplied,
                    // which would give two buttons that both just close.
                    showCancelButton: false,
                }
            )}
        </>
    );
};
